import { useMutation, useQuery } from "@tanstack/react-query";
import { DatePicker, DescriptionsProps, Input, Radio, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { EProgram, EProgramEvent, EProgramEventOutline, EProgramSelectOption, EProgramSelectOptionItem, EProgramSubjectOption, getProgramById } from "../../../../../../../service/program";
import { EProgramBooking, EProgramBookingSelectOption, EProgramBookingSubjectOption, EProgramBookingTicket, getProgramBookingAdminByProgramBookingId, updateProgramBookingAdminByUpdateProgramBookingAdminRequest } from "../../../../../../../service/programBooking";
import { EProgramBookingStatus, programBookingStatusToTextMap, textToProgramBookingStatusMap } from "../../../model";
import { DefaultOptionType } from "antd/es/select";

const getProgramApplyDetailDescriptionItems = (data: IProgramContainer, programBooking: IProgramBooking, setProgramBooking: React.Dispatch<React.SetStateAction<IProgramBooking | undefined>>, isPaidStatusOptions: DefaultOptionType[]): DescriptionsProps["items"] => {
    const status = textToProgramBookingStatusMap[programBooking.status] ?? programBooking.status
    return [
        {
            label: "프로그램",
            children: data.title,
        },
        {
            label: "이용일",
            children: (
                <DatePicker
                    style={{ width: "150px" }}
                    value={dayjs(programBooking.targetDateTime)}
                    disabled={textToProgramBookingStatusMap[programBooking.status] === EProgramBookingStatus.REFUND}
                    disabledDate={e => !data.programEventOutlineList?.flatMap(programEventOutline => programEventOutline.programEventList)?.map(programEven => dayjs(programEven.targetDateTime)?.format('YYYY-MM-DD'))?.includes(e.format('YYYY-MM-DD'))}
                    onChange={(date: Dayjs) => {
                        setProgramBooking((prev) => (prev && {
                            ...prev,
                            targetDateTime: date.toISOString(),
                        }))
                    }
                    }
                />
            ),
        },
        {
            label: "이용 시간",
            children: (
                <Select
                    key={programBooking.targetDateTime}
                    style={{ width: "150px" }}
                    value={programBooking.programEventId}
                    disabled={textToProgramBookingStatusMap[programBooking.status] === EProgramBookingStatus.REFUND}
                    onChange={q =>
                        setProgramBooking(prev => (prev && {
                            ...prev,
                            programEventId: q,
                        }))
                    }
                    options={data.programEventOutlineList?.flatMap(programEventOutline => programEventOutline.programEventList)
                        ?.filter(programEvent => dayjs(programEvent.targetDateTime)?.format('YYYY-MM-DD') === dayjs(programBooking.targetDateTime)?.format('YYYY-MM-DD'))
                        ?.map(q => ({
                            key: q.id,
                            value: q.id,
                            label: dayjs(q.targetDateTime).format('HH:mm'),
                        }))}
                />
            ),
        },
        {
            label: '티켓/인원',
            children: programBooking?.programBookingTicketList?.map((q, idx) => q.count > 0 && <div key={idx}>{q.name + " X " + q.count}</div>)
        },
        ...(data.programSelectOptionList?.map(q => ({
            key: q.id,
            label: q.name,
            children: (
                <Radio.Group
                    value={programBooking.programBookingSelectOptionList?.find(w => w.programSelectOptionId === q.id)?.programSelectOptionItemIdList?.[0]}
                    disabled={textToProgramBookingStatusMap[programBooking.status] === EProgramBookingStatus.REFUND}
                    onChange={e =>
                        setProgramBooking(prev => (prev && {
                            ...prev,
                            programBookingSelectOptionList: prev.programBookingSelectOptionList?.map((option) =>
                                option.programSelectOptionId === q.id
                                    ? { ...option, programSelectOptionItemIdList: [e.target.value] }
                                    : option
                            ),
                        }
                        ))
                    }
                    options={q.programSelectOptionItemList?.map((w) => ({ key: w.id, value: w.id, label: w.name }))}
                />
            ),
        })) ?? []),
        ...(data.programSubjectOptionList?.map((q) => ({
            key: q.id,
            label: q.name,
            children: (
                <Input.TextArea
                    value={programBooking.programBookingSubjectOptionList?.find((w) => w.programSubjectOptionId === q.id)?.content ?? ""}
                    disabled={textToProgramBookingStatusMap[programBooking.status] === EProgramBookingStatus.REFUND}
                    onChange={(e) =>
                        setProgramBooking((prev) => (prev && {
                            ...prev,
                            programBookingSubjectOptionList: prev.programBookingSubjectOptionList?.map((option) =>
                                option.programSubjectOptionId === q.id
                                    ? { ...option, content: e.target.value }
                                    : option
                            ),
                        }))
                    }
                />
            ),
        })) ?? []),
        {
            label: "이름",
            children: programBooking.memberName,
        },
        {
            label: "휴대폰번호",
            children: programBooking.memberMobile,
        },
        {
            label: "예약 상태",
            children: (programBooking.status === "REFUND"
                ? <div>{programBooking.status}</div>
                : <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Select
                        style={{ width: "150px" }}
                        defaultValue={textToProgramBookingStatusMap[programBooking.status]}
                        value={textToProgramBookingStatusMap[programBooking.status] ?? programBooking.status}
                        onChange={(q) => setProgramBooking((prev) => prev && ({ ...prev, status: q, }))}
                        options={data.isPaid
                            ? isPaidStatusOptions
                            : [
                                { label: programBookingStatusToTextMap[EProgramBookingStatus.PENDING], value: EProgramBookingStatus.PENDING },
                                { label: programBookingStatusToTextMap[EProgramBookingStatus.COMPLETE], value: EProgramBookingStatus.COMPLETE },
                                { label: programBookingStatusToTextMap[EProgramBookingStatus.CANCEL], value: EProgramBookingStatus.CANCEL },
                                { label: programBookingStatusToTextMap[EProgramBookingStatus.CHECK_IN], value: EProgramBookingStatus.CHECK_IN },
                                { label: programBookingStatusToTextMap[EProgramBookingStatus.ABSENT], value: EProgramBookingStatus.ABSENT },
                            ]}
                    />
                    <div style={{ fontWeight: 400, fontSize: '12px', color: "#000000A6" }}>
                        ※ 환불은 취소 요청 후 가능합니다
                    </div>
                </div>
            ),
        },
        {
            label: status === EProgramBookingStatus.PENDING || status === EProgramBookingStatus.REQUEST_PAID ? '결제 예정 금액' : "결제 금액",
            children: programBooking.paidAmount?.toLocaleString(),
        },
        {
            label: "신청 일시",
            children: dayjs(programBooking.bookingDateTime ?? '').format('YYYY.MM.DD'),
        },
    ]
};


const defaultProgramFieldListMap: IProgramField = {
    program: [EProgram.id, EProgram.title, EProgram.programPeriod, EProgram.isPaid, EProgram.programEventOutlineList, EProgram.programSelectOptionList, EProgram.programSubjectOptionList],
    [EProgram.programEventOutlineList]: [EProgramEventOutline.id, EProgramEventOutline.day, EProgramEventOutline.startTime, EProgramEventOutline.programEventList],
    [EProgramEventOutline.programEventList]: [EProgramEvent.id, EProgramEvent.targetDateTime, EProgramEvent.maxBookingCnt],
    [EProgram.programSelectOptionList]: [EProgramSelectOption.id, EProgramSelectOption.name, EProgramSelectOption.description, EProgramSelectOption.programSelectOptionItemList],
    [EProgramSelectOption.programSelectOptionItemList]: [EProgramSelectOptionItem.id, EProgramSelectOptionItem.name],
    [EProgram.programSubjectOptionList]: [EProgramSubjectOption.id, EProgramSubjectOption.name, EProgramSubjectOption.description],
}

const useGetProgramForProgramBookingDetailById = (id: number, programField: IProgramField = defaultProgramFieldListMap) => {
    const query = useQuery({
        queryKey: ['getProgramById', id, programField],
        queryFn: () => getProgramById(id, programField),
        enabled: !!id,
        select: res => res.data.data.getProgramById,
    });

    return query;
}

const defaultProgramBookingFieldListMap: IProgramBookingField = {
    programBooking: [EProgramBooking.id, EProgramBooking.programEventId, EProgramBooking.memberName, EProgramBooking.memberMobile, EProgramBooking.status, EProgramBooking.paidAmount, EProgramBooking.targetDateTime, EProgramBooking.bookingDateTime, EProgramBooking.note, EProgramBooking.programBookingSelectOptionList, EProgramBooking.programBookingSubjectOptionList, EProgramBooking.programBookingTicketList],
    [EProgramBooking.programBookingSelectOptionList]: [EProgramBookingSelectOption.id, EProgramBookingSelectOption.programSelectOptionId, EProgramBookingSelectOption.programSelectOptionItemIdList],
    [EProgramBooking.programBookingSubjectOptionList]: [EProgramBookingSubjectOption.id, EProgramBookingSubjectOption.programSubjectOptionId, EProgramBookingSubjectOption.content],
    [EProgramBooking.programBookingTicketList]: [EProgramBookingTicket.name, EProgramBookingTicket.count]
}

const useGetProgramBookingAdminByProgramBookingId = (programBookingId: number, enabled: boolean = true, programBookingField: IProgramBookingField = defaultProgramBookingFieldListMap) => {
    const query = useQuery({
        queryKey: ["getProgramBookingAdminByProgramBookingId", programBookingId],
        queryFn: () => getProgramBookingAdminByProgramBookingId<IProgramBooking>(programBookingId, programBookingField),
        enabled: enabled && !!programBookingId,
        select: res => res.data.data.getProgramBookingAdminByProgramBookingId,
    })
    return query
}

const useUpdateProgramBookingAdminByUpdateProgramBookingAdminRequest = () => {
    const mutate = useMutation({
        mutationFn: updateProgramBookingAdminByUpdateProgramBookingAdminRequest,
    })
    return mutate
}

const getNextAvailableStatuses = (status?: EProgramBookingStatus | string) => {
    const parseStatus = textToProgramBookingStatusMap[status ?? ''] ?? status
    switch (parseStatus) {
        case EProgramBookingStatus.PENDING:
            return [EProgramBookingStatus.PENDING, EProgramBookingStatus.REQUEST_PAID, EProgramBookingStatus.COMPLETE, EProgramBookingStatus.CANCEL];
        case EProgramBookingStatus.REQUEST_PAID:
            return [EProgramBookingStatus.REQUEST_PAID, EProgramBookingStatus.COMPLETE, EProgramBookingStatus.CANCEL];
        case EProgramBookingStatus.COMPLETE:
            return [EProgramBookingStatus.COMPLETE, EProgramBookingStatus.REQUEST_PAID, EProgramBookingStatus.REQUEST_CANCEL, EProgramBookingStatus.CHECK_IN, EProgramBookingStatus.ABSENT];
        case EProgramBookingStatus.ABSENT:
            return [EProgramBookingStatus.ABSENT, EProgramBookingStatus.CHECK_IN, EProgramBookingStatus.REQUEST_CANCEL];
        case EProgramBookingStatus.REQUEST_CANCEL:
            return [EProgramBookingStatus.REQUEST_CANCEL];
        case EProgramBookingStatus.CHECK_IN:
            return [EProgramBookingStatus.CHECK_IN, EProgramBookingStatus.ABSENT, EProgramBookingStatus.REQUEST_CANCEL];
        case EProgramBookingStatus.CANCEL:
            return [EProgramBookingStatus.CANCEL,];
        case EProgramBookingStatus.REFUND:
            return [EProgramBookingStatus.REFUND,];
        default:
            return [];
    }
}

export { getProgramApplyDetailDescriptionItems, useGetProgramForProgramBookingDetailById, useGetProgramBookingAdminByProgramBookingId, useUpdateProgramBookingAdminByUpdateProgramBookingAdminRequest, getNextAvailableStatuses }